<template>
	<VDialogV3
		id="characteristic-card"
		position="right"
		width="600px"
		height="100vh"

		:context-menu="CharacteristicsService.contextMenu"
		:entity="localCharacteristic"

		:submit-method="submit"
		@close="onClose"
	>
		<section>
			<h1>Основное</h1>

			<VInput
				v-model="localCharacteristic.name"
				label="Название"
				layout="vertical"
				required
				:max-length="300"
			/>

			<VTextarea
				v-model="localCharacteristic.description"
				label="Описание"
				layout="vertical"
				:max-length="300"
			/>

			<VSelect
				v-model="localCharacteristic.unit"
				label="Единица измерения"
				layout="vertical"
				:options="unitList"
				:required="isUnitRequired"
				:clearable="isUnitRequired === false"
			/>

			<VSelectV2
				v-model="localCharacteristic.group"
				label="Группа характеристик"
				layout="vertical"
				clearable
				:disabled="groupLocked"
				:data="characteristicsGroupSource"
			/>
		</section>

		<hr>

		<section>
			<h1>Настройки значения</h1>

			<VSelect
				label="Тип значения"
				layout="vertical"
				placeholder="Выберите тип значения"
				required
				v-model="valueTypeOption"
				:options="valueTypeList"
				:disabled="localCharacteristic.isExisting"
				:clearable="false"
			/>

			<template v-if="localCharacteristic.isNumeric">
				<VInputNumber
					label="Диапазон"
					layout="vertical"
					v-model="localCharacteristic.validations.range"
					:min="0"
				/>

				<VInputNumber
					label="Минимальное значение"
					layout="vertical"
					:precision="3"
					v-model="localCharacteristic.validations.min"
					:validate-method="() => localCharacteristic.validateMin()"
				/>

				<VInputNumber
					label="Максимальное значение"
					layout="vertical"
					:precision="3"
					v-model="localCharacteristic.validations.max"
					:validate-method="() => localCharacteristic.validateMax()"
				/>

				<VInputNumber
					label="Точность числа"
					layout="vertical"
					v-model="localCharacteristic.validations.precision"
				/>
			</template>

			<template v-if="localCharacteristic.isString">
				<VInputNumberRus
					label="Минимальная длина"
					layout="vertical"
					:precision="0"
					v-model="localCharacteristic.validations.minLength"
				/>

				<VInputNumberRus
					label="Максимальная длина"
					layout="vertical"
					:precision="0"
					v-model="localCharacteristic.validations.maxLength"
				/>
			</template>

			<template v-if="localCharacteristic.isSelectableMulti">
				<VSelect
					v-model="selectableListFilterModeOption"
					label="Режим фильтрации"
					layout="vertical"
					placeholder="Выберите режим"
					:options="selectableListFilterModeList"
					option-identity="id"
					required
					:clearable="false"
				/>
			</template>

			<div v-if="hasAllowedValuesEditor">
				<div v-if="localCharacteristic.isExisting === false">
					Для создания списка сначала нужно сохранить характеристику
				</div>

				<CharacteristicAllowedValuesEdit
					v-else
					:value="localCharacteristic.allowedValues"
					:characteristic-id="localCharacteristic.id"
					@allowed-value:created="onAllowedValuesChanged"
					@allowed-value:deleted="onAllowedValuesChanged"
				/>
			</div>
		</section>
	</VDialogV3>
</template>

<script lang="ts">

import Characteristic                  from '@/domains/Characteristics/Characteristic';
import CharacteristicsService          from '@/domains/Characteristics/CharacteristicsService';
import CharacteristicGroup             from '@/domains/CharacteristicGroups/CharacteristicGroup';
import CharacteristicUnit              from '@/domains/CharacteristicUnits/CharacteristicUnit';
import CharacteristicUnitsService      from '@/domains/CharacteristicUnits/CharacteristicUnitsService';
import CharacteristicGroupsService     from  '@/domains/CharacteristicGroups/CharacteristicGroupsService'
import CharacteristicAllowedValuesEdit from '@/domains/Characteristics/components/CharacteristicAllowedValuesEdit.vue';

type SelectOption = { id: string; name: string };

export default defineComponent({
	name: 'CharacteristicCard',

	components: {
		CharacteristicAllowedValuesEdit,
	},

	props: {
		/** Готовый объект — передаётся из реестра */
		characteristicOrId: {
			type:    [Characteristic, Number] as PropType<Characteristic | number | undefined>,
			default: undefined,
		},

		/** Группа, выбранная в сайдбаре реестра — подставляем для новой характеристики */
		group: {
			type:    CharacteristicGroup,
			default: null,
		},

		/** ID — карточка сама загружает объект */
		id: {
			type:    Number,
			default: null,
		},
	},

	setup() {
		return {
			CharacteristicsService,
		};
	},

	data() {
		return {
			localCharacteristic: null as Characteristic | null,
			unitList:            [] as CharacteristicUnit[],

			isAllowedValuesChanged: false,

			valueTypeList:                Characteristic.valueTypeList,
			selectableListFilterModeList: Characteristic.selectableListFilterModeList,
			characteristicsGroupSource:   this.$core.loader.select(
				({ pattern }) => CharacteristicGroupsService.getList(pattern).then(r => r)
			).preloadOnMount(),
		};
	},

	computed: {
		isUnitRequired(): boolean {
			if (this.localCharacteristic === null) return false;
			return this.localCharacteristic.isNumeric;
		},

		/** Группа жёстко заблокирована если она передана извне и у нас новая характеристика */
		groupLocked(): boolean {
			return this.group !== null && this.localCharacteristic?.isExisting === false;
		},

		hasAllowedValuesEditor(): boolean {
			if (this.localCharacteristic === null) return false;
			return this.localCharacteristic.isSelectableSingle || this.localCharacteristic.isSelectableMulti;
		},

		/**
		 * Адаптер между моделью (хранит valueType строкой) и VSelect (работает с объектом-опцией).
		 * getter ищет опцию по строке из модели, setter пишет в модель .id выбранной опции.
		 */
		valueTypeOption: {
			get(): SelectOption | null {
				if (this.localCharacteristic === null) return null;
				const found = this.valueTypeList.find(t => t.id === this.localCharacteristic.valueType);
				if (found === undefined) return null;
				return found;
			},
			set(option: SelectOption | null): void {
				if (this.localCharacteristic === null) return;
				if (option === null) {
					this.localCharacteristic.valueType = null;
				}
				else {
					this.localCharacteristic.valueType = option.id;
				}
			},
		},

		/** То же, что valueTypeOption, но для selectableListFilterMode. */
		selectableListFilterModeOption: {
			get(): SelectOption | null {
				if (this.localCharacteristic === null) return null;
				const found = this.selectableListFilterModeList.find(m => m.id === this.localCharacteristic.selectableListFilterMode);
				if (found === undefined) return null;
				return found;
			},
			set(option: SelectOption | null): void {
				if (this.localCharacteristic === null) return;
				if (option === null) {
					this.localCharacteristic.selectableListFilterMode = null;
				}
				else {
					this.localCharacteristic.selectableListFilterMode = option.id;
				}
			},
		},
	},

	async mounted() {
		await this.init();
		await this.loadUnits();
	},

	methods: {
		async init() {
			if (this.characteristicOrId instanceof Characteristic) {
				// Из реестра прилетел готовый объект — клонируем,
				// чтобы мутации в карточке не протекали обратно в реестр.
				this.localCharacteristic = this.$core.utils.cloneDeep(this.characteristicOrId);
			} else if (this.id !== null || typeof this.characteristicOrId === 'number') {
				// Прилетел id (через отдельный проп или под старым именем
				// characteristicOrId как число) — дёргаем сервис.
				this.localCharacteristic = await CharacteristicsService.get(this.id ?? this.characteristicOrId);
			} else if (this.group !== null) {
				// Создаём новую. Если в реестре была выбрана группа — привязываем её сразу.
				this.localCharacteristic = Characteristic.fromGroup(this.group);
			} else {
				this.localCharacteristic = new Characteristic();
			}
		},

		async loadUnits(): Promise<void> {
			this.unitList  = await CharacteristicUnitsService.getList();
		},

		/** Значения уже сохранены на беке — запоминаем, чтобы при закрытии обновить реестр */
		onAllowedValuesChanged(): void {
			this.isAllowedValuesChanged = true;
		},

		/** Список значений сохраняется мимо сабмита — при закрытии обновляем реестр */
		onClose(): void {
			if (this.isAllowedValuesChanged === true) {
				this.$core.dialogs.CharacteristicCard.resolve();
			} else {
				// изменений мимо сабмита не было — обычное закрытие, промис получит false
			}
		},

		// ==================== Submit / Delete ====================

		async submit(): Promise<boolean> {
			if (this.localCharacteristic.isExisting === true) {
				return await CharacteristicsService.update(this.localCharacteristic);
			}
			else {
				return await CharacteristicsService.add(this.localCharacteristic);
			}
		},
	},
});
</script>